/**
 * The sign-in attempt in progress, kept in this browser's local storage so
 * that a reload resumes the code step, and so the magic-link page can tell
 * whether the link was opened on the device that asked for it. It holds
 * the attempt's IDs and a masked address only: never a code.
 */

/** A sign-in attempt waiting for its code. */
export interface SignInAttempt {
  deviceId: string;
  preAuthSessionId: string;
  method: 'EMAIL' | 'SMS';
  /** The address, masked for display (`a•••@example.com`). */
  contact: string;
  /** When the last code was sent (epoch milliseconds). */
  sentAt: number;
  codeLifetimeSeconds: number;
  resendAfterSeconds: number;
  /** Where to go after signing in. */
  next?: string;
}

const KEY = 'hire.signIn';

/** Saves the attempt (storage failures are ignored: the flow still works in this tab). */
export function saveAttempt(attempt: SignInAttempt): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(attempt));
  } catch {
    // Private mode or storage full.
  }
}

function isAttempt(value: unknown): value is SignInAttempt {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.deviceId === 'string' &&
    typeof v.preAuthSessionId === 'string' &&
    (v.method === 'EMAIL' || v.method === 'SMS') &&
    typeof v.contact === 'string' &&
    typeof v.sentAt === 'number' &&
    typeof v.codeLifetimeSeconds === 'number' &&
    typeof v.resendAfterSeconds === 'number'
  );
}

/**
 * The saved attempt, if its code can still work.
 *
 * @param now - The clock (tests).
 */
export function loadAttempt(now = Date.now()): SignInAttempt | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    const value: unknown = raw ? JSON.parse(raw) : undefined;
    if (!isAttempt(value)) return undefined;
    if (now > value.sentAt + value.codeLifetimeSeconds * 1_000) return undefined;
    return value;
  } catch {
    return undefined;
  }
}

/** Forgets the attempt (signed in, or starting again). */
export function clearAttempt(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** An address masked for display: `a•••@example.com`, `+86•••••00`. */
export function maskContact(method: 'EMAIL' | 'SMS', value: string): string {
  if (method === 'EMAIL') {
    const [local = '', domain = ''] = value.split('@');
    return `${local.slice(0, 1)}•••@${domain}`;
  }
  return `${value.slice(0, 3)}•••••${value.slice(-2)}`;
}
